import { ComponentType, ReactNode, useEffect, useRef, useState } from "react";

type LazyComponent = ComponentType<Record<string, never>>;

interface LazySectionProps {
  loader: () => Promise<{ default: LazyComponent }>;
  fallback?: ReactNode;
  rootMargin?: string;
}

export function LazySection({
  loader,
  fallback = null,
  rootMargin = "200px",
}: LazySectionProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [Component, setComponent] = useState<LazyComponent | null>(null);

  useEffect(() => {
    if (!isVisible || Component) {
      return;
    }

    let isCancelled = false;

    loader().then((module) => {
      if (!isCancelled) {
        setComponent(() => module.default);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [Component, isVisible, loader]);

  useEffect(() => {
    const node = containerRef.current;

    if (!node) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [rootMargin]);

  const shouldRenderFallback = !Component;
  const LoadedComponent = Component;

  useEffect(() => {
    if (!LoadedComponent) {
      return;
    }

    let pendingTarget: string | null = null;
    try {
      pendingTarget = sessionStorage.getItem("advelo-pending-scroll");
    } catch {
      pendingTarget = null;
    }

    if (!pendingTarget) {
      return;
    }

    requestAnimationFrame(() => {
      const element = document.getElementById(pendingTarget ?? "");
      if (!element) {
        return;
      }

      element.scrollIntoView({ behavior: "smooth" });
      try {
        sessionStorage.removeItem("advelo-pending-scroll");
      } catch {
        // ignore
      }
    });
  }, [LoadedComponent]);

  return (
    <div ref={containerRef}>
      {LoadedComponent ? <LoadedComponent /> : shouldRenderFallback ? fallback : null}
    </div>
  );
}
